import { Dialog } from "@headlessui/react";
import { ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { SetStateAction, useRef } from "react";
import { Company } from "../../model/Companies";
import { convertDateTimeToString, convertDateTimeToStringCompactFormat } from "../../utility/ConvertUtily";
import userQuery from "../../firestore/User.query";
import { auth } from "../../firebase";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import { requestGetRdbEventParticipationHistory } from "../../api/Messages.api";
import { EventParticipationHistory, EventParticipationHistorySearchParam, RdbEventParticipationHistoryMapping } from "../../model/EventParticipationHistory";

export type EventParticipationExportModalProps = {
  company: Company | undefined;
  targetUserId: string | undefined;
  searchParams: EventParticipationHistorySearchParam;
  stateSetLoadProgress: (value: SetStateAction<number>) => void;
  checkSearchParam: boolean;
}

export const EventParticipationExportModal = ({
  open,
  toggle,
  closeOnClickOutside,
  company,
  targetUserId,
  searchParams,
  stateSetLoadProgress,
  checkSearchParam
}: ModalProp & EventParticipationExportModalProps) => {

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;
  const dispatch = useDispatch();

  const csvDataLength = useRef<number>(10000);

  const csvCol = [
    "利用者番号",
    "名前",
    "タイトル",
    "会場",
    "配信日時",
    "イベント開催日時",
    "利用者の回答",
    "出欠結果"
  ];

  async function exportCsvHandler () {
    toggle();
    setLoadProgress(0);
    const result = await requestGetRdbEventParticipationHistory(currentUser?.tenantId!, company?.displayId!, -1, 1, searchParams);
    if (result.success) {
      if (result.data.count > 0) {
        const csvResult = await exportCsv(result.data.rows.map((item: any) => RdbEventParticipationHistoryMapping(item)));
        console.log(csvResult);
      } else {
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_EXPORT_HISTORY_NO_DATA, "danger")));
      }
    } else {
      console.log(result.data);
      dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
    }
    setLoadProgress(-1);
  }

  async function exportCsv (rowData: EventParticipationHistory[]) {
    try{
      const csvData = rowData.map((value) => {
        return [
          value.userDisplayId,
          value.lastName,
          value.title,
          value.eventVenue ?? '',
          convertDateTimeToString(value.sendDate),
          convertDateTimeToString(value.eventAt),
          value.replyButtonName,
          value.attendanceButtonName?.replace('　', '') ?? ''
        ]
      });

      // 出力データを出力件数ずつ分割する
      const csvDataList = csvData.reduce((preArr: any[][], current, index) => {
        if (index % csvDataLength.current) { return preArr } else { return [...preArr, ...[csvData.slice(index, index + csvDataLength.current)]] }
      },[] as any[][]);

      let targetUserName = "";
      if (targetUserId !== undefined) {
        // 利用者詳細画面から履歴出力する場合
        const targetUser = await userQuery.getUser(currentUser!.tenantId!, company?.displayId + targetUserId);
        targetUserName = targetUser.lastName ?? "";
      }

      for (let i = 0; i < csvDataList.length ; i++) {
        // 文字列のエスケープ処理
        const escapeCsvData = csvDataList[i].map((value: any[], index: number) => {
          setLoadProgress((index / rowData.length * 100) / 2);
          return value.map((val) => {
            return `"${val.replace(/"/g, '""')}"`
          })
        })
        const escapeCsvCol = csvCol.map((value) => {
          return `"${value.replace(/"/g, '""')}"`
        })

        // カラムとデータを連結
        const csvStr = escapeCsvCol.join(',') + "\n" + escapeCsvData.map((data: any[]) => data.join(',')).join('\n');

        // ファイル名
        let filename = `イベント参加履歴_${company!.name}_${convertDateTimeToStringCompactFormat(new Date())}${csvDataList.length > 1 ? '_' + (i + 1) : ''}.csv`;
        if (targetUserName !== "") {
          // 利用者詳細画面から履歴出力する場合
          filename = `イベント参加履歴_${company!.name}_${targetUserName}_${convertDateTimeToStringCompactFormat(new Date())}${csvDataList.length > 1 ? '_' + (i + 1) : ''}.csv`;
        }

        const a = document.createElement('a');
        // BOM
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, csvStr], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.id = "download-anchor";
        a.style.display = 'none';

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.setAttribute('sandbox', 'allow-same-origin allow-downloads');

        document.body.appendChild(iframe);
        if (iframe.contentWindow) {
          const iDoc = iframe.contentWindow.document;
          iDoc.write(a.outerHTML);
          const targetAnchor = iDoc.getElementById('download-anchor');
          targetAnchor?.click();
        }
        iframe.remove();

        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 500);
      }
      if(csvDataList.length === 0){
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_EXPORT_HISTORY_NO_DATA, "danger")));
      }

      return {result: true, details: undefined};
    } catch (err) {
      return {result: false, details: err};
    }
  }

  function setLoadProgress (percentage: number) {
    stateSetLoadProgress(percentage);
  }

  return (
    <Modal
      open={open}
      toggle={toggle}
      closeOnClickOutside={closeOnClickOutside}
    >
      <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all">
        <div className="px-2 pb-4 sm:p-4">
          <div className="text-center sm:mt-3">
            <Dialog.Title as="h3" className="text-left text-lg font-medium leading-6 text-gray-900">
              イベント参加履歴出力確認
            </Dialog.Title>
          </div>
          <div className="mt-5">
            イベント参加履歴の検索結果情報をCSVファイルで出力します。<br />
            <div className="text-red-500">
              ※CSV出力は、対象データの件数によって<span className="font-semibold">作業時間が長時間になる場合があります。</span><br/>
              ※一度実行すると中止することはできません。
            </div>
          </div>
        </div>
        <ModalFoterBox>
          <Button
            className="w-[132px] mt-1 mr-2"
            outline
            onClick={toggle}
            children={"キャンセル"}
          />
          <Button
            className="w-[132px] mt-1 mr-2"
            onClick={() => {
              if (!checkSearchParam) {
                toggle();
                dispatch(alertOpen(AlertValue(ConstMessage.ERR_EXPORT_HISTORY_NO_DATA, "danger")));
              } else {
                exportCsvHandler();
              }
            }}
            children={"OK"}
          />
        </ModalFoterBox>
      </Dialog.Panel>
    </Modal>
  );
}
